import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Row, 
  Col, 
  Button, 
  Tag, 
  Table, 
  message, 
  Modal, 
  Select, 
  Progress, 
  Alert, 
  Spin,
  Typography,
  Space,
  Divider,
  Tooltip
} from 'antd';
import { 
  ReloadOutlined, 
  PlayCircleOutlined, 
  DownloadOutlined, 
  ToolOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  InfoCircleOutlined,
  SyncOutlined,
  DesktopOutlined,
  GlobalOutlined
} from '@ant-design/icons';
import { withTranslation } from 'react-i18next';
import WaterLinkService from '../../services/WaterLinkService';
import ElectronWaterLinkService from '../../services/ElectronWaterLinkService';
import WaterLinkTest from './WaterLinkTest';

const { Title, Text } = Typography;
const { Option } = Select;

const SpintouchManagementAdaptive = ({ t }) => {
  // États principaux
  const [loading, setLoading] = useState(false);
  const [serviceStatus, setServiceStatus] = useState(null);
  const [deviceStatus, setDeviceStatus] = useState(null);
  const [testOptions, setTestOptions] = useState(null);
  const [testResults, setTestResults] = useState(null);
  const [logs, setLogs] = useState(null);
  
  // États pour les tests
  const [selectedReagent, setSelectedReagent] = useState(null);
  const [selectedSample, setSelectedSample] = useState(null);
  const [testInProgress, setTestInProgress] = useState(false);
  const [testProgress, setTestProgress] = useState(0);
  
  // États pour les modales
  const [showTestModal, setShowTestModal] = useState(false);
  const [showLogsModal, setShowLogsModal] = useState(false);
  const [showResultsModal, setShowResultsModal] = useState(false);

  // Détecter l'environnement
  const [isElectron, setIsElectron] = useState(false);
  const [service, setService] = useState(null);

  useEffect(() => {
    // Détecter si on est dans Electron
    const electronDetected = ElectronWaterLinkService.isElectronApp();
    setIsElectron(electronDetected);
    setService(electronDetected ? ElectronWaterLinkService : WaterLinkService);
    
    if (electronDetected) {
      console.log('[Spintouch] Mode Electron détecté');
    } else {
      console.log('[Spintouch] Mode navigateur détecté');
    }
  }, []);

  // Charger les données initiales
  useEffect(() => {
    if (service) {
      loadInitialData();
      // Actualiser toutes les 5 secondes
      const interval = setInterval(loadInitialData, 5000);
      return () => clearInterval(interval);
    }
  }, [service]);

  const loadInitialData = async () => {
    if (!service) return;
    
    try {
      setLoading(true);
      
      // Vérifier le service
      const serviceInfo = await service.checkService();
      setServiceStatus(serviceInfo);
      
      // Obtenir le statut de l'appareil
      const status = await service.getStatus();
      setDeviceStatus(status);
      
      // Si l'appareil peut faire des tests, charger les options
      if (status.CanDoWatertest) {
        const options = await service.getOptions();
        setTestOptions(options);
      }
      
    } catch (error) {
      message.error('Erreur de connexion au service WaterLink: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRunTest = async () => {
    if (!service) return;
    
    if (!selectedReagent || !selectedSample) {
      message.warning('Veuillez sélectionner un réactif et un échantillon');
      return;
    }

    try {
      setTestInProgress(true);
      setTestProgress(0);
      
      // Simuler le progrès du test
      const progressInterval = setInterval(() => {
        setTestProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return prev;
          }
          return prev + 10;
        });
      }, 1000);

      const results = await service.runWaterTest(selectedReagent, selectedSample);
      
      clearInterval(progressInterval);
      setTestProgress(100);
      setTestResults(results);
      setShowTestModal(false);
      setShowResultsModal(true);
      
      message.success('Test terminé avec succès');
      
    } catch (error) {
      message.error('Erreur lors du test: ' + error.message);
    } finally {
      setTestInProgress(false);
      setTestProgress(0);
    }
  };

  const handleUpdateFirmware = async () => {
    if (!service) return;
    
    Modal.confirm({
      title: 'Mise à jour du firmware',
      content: 'Une mise à jour du firmware est disponible. Cette opération peut prendre 5-7 minutes et l\'appareil redémarrera plusieurs fois. Voulez-vous continuer ?',
      okText: 'Oui',
      cancelText: 'Non',
      onOk: async () => {
        try {
          setLoading(true);
          await service.updateFirmware();
          message.success('Mise à jour du firmware lancée');
          loadInitialData();
        } catch (error) {
          message.error('Erreur lors de la mise à jour: ' + error.message);
        } finally {
          setLoading(false);
        }
      }
    });
  };

  const handleCalibrate = async () => {
    if (!service) return;
    
    Modal.confirm({
      title: 'Calibration de l\'appareil',
      content: 'Veuillez placer le disque de vérification du compteur dans l\'appareil avant de lancer la calibration.',
      okText: 'Lancer la calibration',
      cancelText: 'Annuler',
      onOk: async () => {
        try {
          setLoading(true);
          await service.calibrate();
          message.success('Calibration lancée');
          loadInitialData();
        } catch (error) {
          message.error('Erreur lors de la calibration: ' + error.message);
        } finally {
          setLoading(false);
        }
      }
    });
  };

  const handleGetLogs = async () => {
    if (!service) return;
    
    try {
      setLoading(true);
      const logsData = await service.getLogs();
      setLogs(logsData);
      setShowLogsModal(true);
    } catch (error) {
      message.error('Erreur lors de la récupération des logs: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const getCompatibleSamples = () => {
    if (!testOptions || !selectedReagent) return [];
    
    const reagent = testOptions.ReagentTypes.find(r => r.Value === selectedReagent);
    if (!reagent) return [];
    
    return testOptions.SampleTypes.filter(sample => 
      sample.Tags.some(tag => reagent.Tags.includes(tag))
    );
  };

  const renderDeviceStatus = () => {
    if (!deviceStatus) return null;

    const statusText = service.getStatusText(deviceStatus.Status);
    const statusColor = service.getStatusColor(deviceStatus.Status);

    return (
      <Card title="État de l'appareil" size="small">
        <Row gutter={[16, 16]}>
          <Col span={12}>
            <Space direction="vertical" size="small">
              <div>
                <Text strong>Nom: </Text>
                <Text>{deviceStatus.Name || 'Non connecté'}</Text>
              </div>
              <div>
                <Text strong>Statut: </Text>
                <Tag color={statusColor}>{statusText}</Tag>
              </div>
              <div>
                <Text strong>Firmware actuel: </Text>
                <Text>{deviceStatus.CurrentFirmwareString || 'N/A'}</Text>
              </div>
            </Space>
          </Col>
          <Col span={12}>
            <Space direction="vertical" size="small">
              <div>
                <Text strong>Firmware disponible: </Text>
                <Text>{deviceStatus.AvailFirmwareString || 'N/A'}</Text>
              </div>
              <div>
                <Text strong>Progrès: </Text>
                <Text>{deviceStatus.Progress}%</Text>
              </div>
              <div>
                <Text strong>Capacités: </Text>
                <Space wrap>
                  {deviceStatus.CanDoWatertest && <Tag color="green">Test d'eau</Tag>}
                  {deviceStatus.CanGetLog && <Tag color="blue">Logs</Tag>}
                  {deviceStatus.CanDoUpdate && <Tag color="orange">Mise à jour</Tag>}
                  {deviceStatus.CanDoCalibrate && <Tag color="purple">Calibration</Tag>}
                </Space>
              </div>
            </Space>
          </Col>
        </Row>
      </Card>
    );
  };

  const renderTestResults = () => {
    if (!testResults || !testResults.Results) return null;

    const columns = [
      {
        title: 'Paramètre',
        dataIndex: 'Name',
        key: 'name',
      },
      {
        title: 'Valeur',
        dataIndex: 'Value',
        key: 'value',
        render: (value, record) => (
          <Space>
            <Text>{value.toFixed(record.Decimals)}</Text>
            <Text type="secondary">{record.Units}</Text>
            <Tag color={record.State === '=' ? 'green' : record.State === '+' ? 'red' : 'orange'}>
              {record.State}
            </Tag>
          </Space>
        ),
      },
      {
        title: 'Facteur',
        dataIndex: 'FactorCode',
        key: 'factor',
        render: (code) => service.getFactorName(code),
      },
      {
        title: 'Heure',
        dataIndex: 'TimeStamp',
        key: 'timestamp',
      },
    ];

    return (
      <Table
        columns={columns}
        dataSource={testResults.Results}
        pagination={false}
        size="small"
        rowKey="Tag"
      />
    );
  };

  const renderLogs = () => {
    if (!logs || !logs.Results) return null;

    const columns = [
      {
        title: 'Paramètre',
        dataIndex: 'Name',
        key: 'name',
      },
      {
        title: 'Valeur',
        dataIndex: 'Value',
        key: 'value',
        render: (value, record) => (
          <Space>
            <Text>{value.toFixed(record.Decimals)}</Text>
            <Text type="secondary">{record.Units}</Text>
          </Space>
        ),
      },
      {
        title: 'Heure',
        dataIndex: 'TimeStamp',
        key: 'timestamp',
      },
    ];

    return (
      <Table
        columns={columns}
        dataSource={logs.Results}
        pagination={{ pageSize: 10 }}
        size="small"
        rowKey="Tag"
      />
    );
  };

  if (!service) {
    return (
      <div style={{ padding: '24px', textAlign: 'center' }}>
        <Spin size="large" />
        <div style={{ marginTop: 16 }}>
          <Text>Initialisation du service WaterLink...</Text>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: '24px' }}>
      <Title level={2}>
        Gestion Spintouch
        <Tag 
          color={isElectron ? 'blue' : 'green'} 
          style={{ marginLeft: 16 }}
          icon={isElectron ? <DesktopOutlined /> : <GlobalOutlined />}
        >
          {isElectron ? 'Mode Logiciel' : 'Mode Navigateur'}
        </Tag>
      </Title>
      
      <Spin spinning={loading}>
        <Row gutter={[16, 16]}>
          {/* Test de connectivité */}
          <Col span={24}>
            <WaterLinkTest />
          </Col>

          {/* État de l'appareil */}
          <Col span={24}>
            {renderDeviceStatus()}
          </Col>

          {/* Actions principales */}
          <Col span={24}>
            <Card title="Actions" size="small">
              <Space wrap>
                <Button 
                  icon={<ReloadOutlined />} 
                  onClick={loadInitialData}
                  loading={loading}
                >
                  Actualiser
                </Button>
                
                {deviceStatus?.CanDoWatertest && (
                  <Button 
                    type="primary"
                    icon={<PlayCircleOutlined />} 
                    onClick={() => setShowTestModal(true)}
                  >
                    Lancer un test
                  </Button>
                )}
                
                {deviceStatus?.CanGetLog && (
                  <Button 
                    icon={<DownloadOutlined />} 
                    onClick={handleGetLogs}
                  >
                    Voir les logs
                  </Button>
                )}
                
                {deviceStatus?.CanDoUpdate && (
                  <Button 
                    type="primary"
                    danger
                    icon={<SyncOutlined />} 
                    onClick={handleUpdateFirmware}
                  >
                    Mettre à jour le firmware
                  </Button>
                )}
                
                {deviceStatus?.CanDoCalibrate && (
                  <Button 
                    icon={<ToolOutlined />} 
                    onClick={handleCalibrate}
                  >
                    Calibrer
                  </Button>
                )}
              </Space>
            </Card>
          </Col>

          {/* Alertes */}
          {deviceStatus?.Status === 4 && (
            <Col span={24}>
              <Alert
                message="Mise à jour requise"
                description="Une mise à jour du firmware est obligatoire pour utiliser l'appareil."
                type="error"
                showIcon
                action={
                  <Button size="small" danger onClick={handleUpdateFirmware}>
                    Mettre à jour maintenant
                  </Button>
                }
              />
            </Col>
          )}

          {deviceStatus?.Status === 2 && (
            <Col span={24}>
              <Alert
                message="Mise à jour en cours"
                description="Ne débranchez pas l'appareil pendant la mise à jour. Cette opération peut prendre 5-7 minutes."
                type="warning"
                showIcon
              />
            </Col>
          )}
        </Row>
      </Spin>

      {/* Modal de test d'eau */}
      <Modal
        title="Lancer un test d'eau"
        open={showTestModal}
        onCancel={() => setShowTestModal(false)}
        onOk={handleRunTest}
        okText="Lancer le test"
        cancelText="Annuler"
        okButtonProps={{ disabled: !selectedReagent || !selectedSample || testInProgress }}
      >
        <Space direction="vertical" style={{ width: '100%' }} size="large">
          <div>
            <Text strong>Sélectionner le type de réactif:</Text>
            <Select
              style={{ width: '100%', marginTop: 8 }}
              placeholder="Choisir un réactif"
              value={selectedReagent}
              onChange={setSelectedReagent}
            >
              {testOptions?.ReagentTypes?.map(reagent => (
                <Option key={reagent.Value} value={reagent.Value}>
                  {reagent.Name}
                </Option>
              ))}
            </Select>
          </div>

          <div>
            <Text strong>Sélectionner le type d'échantillon:</Text>
            <Select
              style={{ width: '100%', marginTop: 8 }}
              placeholder="Choisir un échantillon"
              value={selectedSample}
              onChange={setSelectedSample}
              disabled={!selectedReagent}
            >
              {getCompatibleSamples().map(sample => (
                <Option key={sample.Value} value={sample.Value}>
                  {sample.Name}
                </Option>
              ))}
            </Select>
          </div>

          {testInProgress && (
            <div>
              <Text strong>Test en cours...</Text>
              <Progress percent={testProgress} />
            </div>
          )}
        </Space>
      </Modal>

      {/* Modal des résultats */}
      <Modal
        title="Résultats du test"
        open={showResultsModal}
        onCancel={() => setShowResultsModal(false)}
        footer={[
          <Button key="close" onClick={() => setShowResultsModal(false)}>
            Fermer
          </Button>
        ]}
        width={800}
      >
        {renderTestResults()}
      </Modal>

      {/* Modal des logs */}
      <Modal
        title="Logs de l'appareil"
        open={showLogsModal}
        onCancel={() => setShowLogsModal(false)}
        footer={[
          <Button key="close" onClick={() => setShowLogsModal(false)}>
            Fermer
          </Button>
        ]}
        width={800}
      >
        {renderLogs()}
      </Modal>
    </div>
  );
};

export default withTranslation()(SpintouchManagementAdaptive);

